import { splitNameKey, validNameKey } from '../../contracts/namekeys'
import type { LegacyRough } from '../model/types'

/** Additive editor metadata. The legacy engine still sees a local component,
 * and its validator/saver need no changes. The request adapter carries scope. */
export function editorNameScope(rough: LegacyRough): { scope: string; parent: string } {
  if (!rough.is_msg) return { scope: '', parent: '' }
  const name = String(rough.inputs_values?.body_textarea?.[0] ?? '')
  const existing = splitNameKey(name)
  const isExisting = rough.is_update || Number(rough.draft_el_id) > 0
  const parentName = String(rough.static_rough?.msg_data?.msg_inputs?.body_textarea?.[0] ?? '')
  const parent = isExisting ? existing.scope : (validNameKey(parentName) ? parentName : '')
  if (typeof rough.name_scope !== 'string') rough.name_scope = isExisting ? existing.scope : ''
  // Retain the candidate when an existing full name has been projected to
  // its local component. This lives on the rough, scoped to that editor.
  if (typeof rough.name_scope_parent !== 'string') rough.name_scope_parent = parent || rough.name_scope
  const scope = rough.name_scope as string
  if (scope) {
    // Translate both sides of legacy's dirty comparison. Otherwise its hidden
    // extended field looks renamed merely because React displays a local name,
    // and can mask a real edit made in the visible compact field.
    for (const values of [rough.inputs_values, rough.saved_inputs_values]) {
      const text = values?.body_textarea
      if (!text) continue
      for (const index of [0, 3]) {
        if (typeof text[index] === 'string') text[index] = localEditorName(text[index] as string, scope)
      }
    }
    for (const key of ['name_key', 'name_key_smart']) {
      const state = rough.el_rough?.[key]
      if (typeof state?.ok_value === 'string') state.ok_value = localEditorName(state.ok_value, scope)
    }
  }
  return { scope: rough.name_scope as string, parent: rough.name_scope_parent as string }
}

export function localEditorName(value: string | undefined, scope: string): string | undefined {
  return scope && value?.startsWith(scope + '@') ? value.slice(scope.length + 1) : value
}

export function addEditorNameScope(action: string, params: Record<string, unknown>, rough: LegacyRough | undefined): void {
  if (!rough || (action !== 'save_msg' && action !== 'check_keys')) return
  const { scope } = editorNameScope(rough)
  if (!scope) return
  // Recipient lookup and code-only availability checks are unrelated.
  if (action === 'check_keys' && (!params.name_key || params.to_address)) return
  params.name_scope = scope
  if (action === 'check_keys') params.access_edit_pin = rough.static_rough?.msg_data?.edit_pin || '0'
  if (rough.is_update) params.name_target = rough.msg_data?.code_key || rough.static_rough?.msg_data?.code_key || ''
  if (action === 'save_msg' && typeof params.more_data === 'string') {
    try {
      const more = JSON.parse(params.more_data)
      if (typeof more.name_key === 'string') more.name_key = localEditorName(more.name_key, scope)
      params.more_data = JSON.stringify(more)
    } catch { /* The normal server parser reports malformed data. */ }
  }
}

